import React from 'react';

import UFOSegment from '@atlaskit/react-ufo/segment';

import { useSmartLinkConnector } from '../../../common/hooks/useSmartLinkConnector';
import type { useUnconnectedSourceTip } from '../../../common/hooks/useUnconnectedSourceTip';

import { ConnectorSourceConnectionTip } from './ConnectorSourceConnectionTip';

type ConnectorTipProps = {
	unconnectedSourceTip: ReturnType<typeof useUnconnectedSourceTip>['unconnectedSourceTip'];
	dismissTip: ReturnType<typeof useUnconnectedSourceTip>['dismissTip'];
};

/**
 * Renders a single connector connection tip with priority:
 * - Reactive tip (user typed an app name) takes priority over proactive (page SmartLinks).
 */
export const ConnectorTip = ({
	unconnectedSourceTip,
	dismissTip,
}: ConnectorTipProps): React.JSX.Element | null => {
	const proactiveSmartLinkConnector = useSmartLinkConnector();

	const activeTip = unconnectedSourceTip
		? { connector: unconnectedSourceTip, tipType: 'reactive' as const, onConnected: dismissTip }
		: proactiveSmartLinkConnector
			? {
					connector: proactiveSmartLinkConnector,
					tipType: 'proactive' as const,
					onConnected: undefined,
				}
			: null;

	if (!activeTip) {
		return null;
	}

	return (
		<UFOSegment name="connector-tip">
			<ConnectorSourceConnectionTip
				friendlyName={activeTip.connector.friendlyName}
				appSource={activeTip.connector.source}
				outboundAuthUrl={activeTip.connector.outboundAuthUrl ?? ''}
				tipType={activeTip.tipType}
				onConnected={activeTip.onConnected}
			/>
		</UFOSegment>
	);
};
